import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["list", "template", "submit"];

  connect() {
    this.boundCloseOnOutside = this.closeOnOutside.bind(this);
    document.addEventListener("click", this.boundCloseOnOutside, true);

    this.boundOnIdTypeChanged = this.onIdTypeChanged.bind(this);
    this.element.addEventListener(
      "invite-producers-id-type-changed",
      this.boundOnIdTypeChanged,
    );

    if (this.hasSubmitTarget && this.hasListTarget) {
      this.boundSyncContinue = () => this.syncContinueButton();
      this.element.addEventListener("input", this.boundSyncContinue);
      this.element.addEventListener("change", this.boundSyncContinue);
      this.syncContinueButton();
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutside, true);
    this.element.removeEventListener(
      "invite-producers-id-type-changed",
      this.boundOnIdTypeChanged,
    );
    if (this.boundSyncContinue) {
      this.element.removeEventListener("input", this.boundSyncContinue);
      this.element.removeEventListener("change", this.boundSyncContinue);
    }
  }

  onIdTypeChanged() {
    if (!this.hasListTarget || !this.hasTemplateTarget) return;

    const sourceRow = this.templateTarget.querySelector("tr");
    if (!sourceRow) return;

    this.listTarget.innerHTML = "";
    this.listTarget.appendChild(this._cloneEmptyRow(sourceRow));
    this._notifyRowsChanged();
    this.syncContinueButton();
  }

  addRow(event) {
    event.preventDefault();

    if (!this.hasTemplateTarget) return;

    const sourceRow = this.templateTarget.querySelector("tr");
    if (!sourceRow) return;

    this.listTarget.appendChild(this._cloneEmptyRow(sourceRow));

    const firstInput = this._firstIdInput(
      this.listTarget.lastElementChild,
    );
    firstInput?.focus();
    this._notifyRowsChanged();
    this.syncContinueButton();
  }

  removeRow(event) {
    event.preventDefault();

    const row = event.currentTarget.closest("[data-invite-row]");
    if (!row || !this.listTarget.contains(row)) return;

    const rowSelector = this.listTarget.querySelectorAll("[data-invite-row]");
    if (rowSelector.length <= 1) return;

    row.remove();
    this.element.dispatchEvent(
      new CustomEvent("invite-producers-row-removed", { bubbles: true }),
    );
    this.syncContinueButton();
  }

  enableEdit(event) {
    event.preventDefault();

    const row = event.target.closest("[data-invite-row]");
    if (!row) return;

    const view = row.querySelector("[data-invite-row-view]");
    const edit = row.querySelector("[data-invite-row-edit]");
    if (!view || !edit) return;

    this.element.querySelectorAll("[data-invite-row]").forEach((r) => {
      if (r !== row) {
        const otherEdit = r.querySelector("[data-invite-row-edit]");
        if (otherEdit && !otherEdit.classList.contains("hidden")) {
          this.closeRowEdit(r);
        }
      }
    });

    view.classList.add("hidden");
    edit.classList.remove("hidden");

    this._firstIdInput(edit)?.focus();
  }

  closeOnOutside(event) {
    const openEdit = this.element.querySelector(
      "[data-invite-row-edit]:not(.hidden)",
    );
    if (!openEdit) return;

    const row = openEdit.closest("[data-invite-row]");
    if (!row) return;

    if (row.contains(event.target)) return;

    this.closeRowEdit(row);
  }

  closeRowEdit(row) {
    if (!row) return;

    const view = row.querySelector("[data-invite-row-view]");
    const edit = row.querySelector("[data-invite-row-edit]");
    if (!view || !edit) return;

    view.classList.remove("hidden");
    edit.classList.add("hidden");
  }

  currentIdType() {
    return (
      this.element.querySelector('input[name="invite[id_type]"]')?.value ||
      "facility"
    );
  }

  hasCompleteRow() {
    if (!this.hasListTarget) return false;
    if (!this._buyerSelected()) return false;

    const idType = this.currentIdType();

    for (const row of this.listTarget.querySelectorAll("[data-invite-row]")) {
      const email =
        row.querySelector('input[name*="[email]"]')?.value?.trim() ?? "";

      if (idType === "matrix") {
        const facilityId =
          row.querySelector('input[name*="[facility_id]"]')?.value?.trim() ??
          "";
        const matrixId =
          row.querySelector('input[name*="[calculation_id]"]')?.value?.trim() ??
          "";
        if (facilityId && matrixId) return true;
      } else {
        const fid =
          row.querySelector('input[name*="[facility_id]"]')?.value?.trim() ??
          "";
        if (fid && email) return true;
      }
    }
    return false;
  }

  syncContinueButton() {
    if (!this.hasSubmitTarget || !this.hasListTarget) return;

    const ok = this.hasCompleteRow();
    this.submitTarget.disabled = !ok;
  }

  _cloneEmptyRow(sourceRow) {
    const row = sourceRow.cloneNode(true);
    row.querySelectorAll("input").forEach((input) => {
      input.value = "";
    });
    row.querySelectorAll("[role='alert']").forEach((el) => el.remove());
    return row;
  }

  _firstIdInput(container) {
    if (!container) return null;

    if (this.currentIdType() === "matrix") {
      return container.querySelector('input[name*="[facility_id]"]');
    }

    return container.querySelector('input[name*="[facility_id]"]');
  }

  _notifyRowsChanged() {
    this.element.dispatchEvent(
      new CustomEvent("invite-producers-rows-changed", { bubbles: true }),
    );
  }

  _buyerSelected() {
    const select = this.element.querySelector('select[name="invite[buyer_id]"]');
    if (!select) return true;

    return select.value.trim() !== "";
  }
}
